import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { useAppState } from '../../context/AppStateContext';
import {
    DocumentTextIcon,
    InformationCircleIcon,
    StarIcon,
    ChevronDownIcon,
    MagnifyingGlassIcon,
    ArrowDownTrayIcon,
    ChartBarIcon,
    TrophyIcon,
    ListBulletIcon,
    TableCellsIcon,
    AcademicCapIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import { generateDetailedReportPDF } from '../../utils/pdfGenerator';
import { getLevelData, getCategoryMin } from '../../utils/cbcUtils';

const SuccessHub = () => {
    const { user } = useAuth();
    const { get } = useApi();
    const { showToast, performanceBands } = useAppState();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [terms, setTerms] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [termsLoaded, setTermsLoaded] = useState(false);
    const [viewMode, setViewMode] = useState('matrix'); // 'matrix' or 'history'
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) setViewMode('history');
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchReport = useCallback(async () => {
        if (!user?.id) return;
        try {
            setLoading(true);
            const url = selectedTerm
                ? `/api/cbc/reports/student/${user.id}/?term=${selectedTerm}`
                : `/api/cbc/reports/student/${user.id}/`;
            const data = await get(url);
            setReportData(data);
        } catch (error) {
            console.error('Error fetching report:', error);
            showToast('Failed to load progress report', 'error');
        } finally {
            setLoading(false);
        }
    }, [user, get, showToast, selectedTerm]);

    const fetchTerms = useCallback(async () => {
        try {
            const [allTerms, allYears, activeInfo] = await Promise.all([
                get('/api/academic-terms/'),
                get('/api/academic-years/'),
                get('/api/auth/active-term/')
            ]);
            setTerms(allTerms || []);
            setAcademicYears(allYears || []);

            if (activeInfo?.term?.id) {
                const activeTerm = allTerms.find(t => t.id === activeInfo.term.id);
                if (activeTerm) {
                    setSelectedYear(activeTerm.year.toString());
                    setSelectedTerm(activeTerm.id.toString());
                }
            } else if (allYears?.length > 0) {
                const currentYear = allYears.find(y => y.is_current) || allYears[0];
                setSelectedYear(currentYear.id.toString());
            }
            setTermsLoaded(true);
        } catch (error) {
            console.error('Error fetching terms/years:', error);
        }
    }, [get]);

    useEffect(() => {
        fetchTerms();
    }, [fetchTerms]);

    useEffect(() => {
        if (termsLoaded) {
            fetchReport();
        }
    }, [fetchReport, termsLoaded]);

    const handleDownloadReport = async () => {
        if (!reportData) return;
        try {
            generateDetailedReportPDF(reportData);
            showToast('Report Card PDF generated successfully!');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('Failed to generate PDF report', 'error');
        }
    };

    const getGradeColor = (level) => {
        const data = getLevelData(level);
        return `${data.bg} ${data.color} ${data.border}`;
    };



    const SubStrandCard = ({ subStrand }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const totalOutcomes = subStrand.outcomes?.length || 0;
        const masteredOutcomes = subStrand.outcomes?.filter(o => ['EE', 'ME'].includes(o.competency_level)).length || 0;
        const masteryPercentage = totalOutcomes > 0 ? Math.round((masteredOutcomes / totalOutcomes) * 100) : 0;

        return (
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-indigo-900/5 transition-all overflow-hidden group">
                <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                            <h4 className="text-lg font-black text-[#18216D] tracking-tight group-hover:text-blue-600 transition-colors uppercase">
                                {subStrand.name}
                            </h4>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                                {totalOutcomes} Core Competencies
                            </p>
                        </div>
                        <div className="relative h-16 w-16 flex-shrink-0">
                            <svg className="h-full w-full rotate-[-90deg]">
                                <circle cx="32" cy="32" r="28" fill="transparent" stroke="#f1f5f9" strokeWidth="6" />
                                <circle
                                    cx="32" cy="32" r="28" fill="transparent"
                                    stroke={
                                        masteryPercentage >= getCategoryMin('EE', performanceBands) ? getLevelData('EE').hex : 
                                        masteryPercentage >= getCategoryMin('ME', performanceBands) ? getLevelData('ME').hex : 
                                        getLevelData('AE').hex
                                    }
                                    strokeWidth="6"
                                    strokeDasharray={`${2 * Math.PI * 28}`}
                                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - masteryPercentage / 100)}`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-[#18216D]">
                                {masteredOutcomes}/{totalOutcomes}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex -space-x-2">
                            {subStrand.outcomes?.slice(0, 3).map((o, i) => (
                                <div key={i} className={`h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold ${getGradeColor(o.competency_level)}`}>
                                    {o.competency_level || '?'}
                                </div>
                            ))}
                            {totalOutcomes > 3 && (
                                <div className="h-6 w-6 rounded-full border-2 border-white bg-slate-50 flex items-center justify-center text-[8px] font-bold text-slate-400">
                                    +{totalOutcomes - 3}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-[#18216D] hover:bg-slate-50 p-2 rounded-xl transition-all"
                        >
                            <ChevronDownIcon className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>

                <div className={`grid-rows-transition ${isExpanded ? 'grid-rows-expanded' : ''}`}>
                    <div className="overflow-hidden">
                        <div className="px-8 pb-8 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="pt-4 border-t border-slate-50">
                                {subStrand.outcomes?.map((outcome) => (
                                    <div key={outcome.code} className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl mb-2 hover:bg-slate-50 transition-colors">
                                        <div className="flex-1 min-w-0 mr-4">
                                            <p className="text-[11px] font-bold text-slate-600 leading-tight">{outcome.outcome}</p>
                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mt-0.5">{outcome.code}</p>
                                        </div>
                                        <div className={`px-2 py-1 rounded-lg border text-[8px] font-black uppercase tracking-widest ${getGradeColor(outcome.competency_level)}`}>
                                            {outcome.competency_level || 'N/A'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const LearningAreaAccordion = ({ area }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const totalOutcomesArea = area.strands?.reduce((acc, strand) =>
            acc + (strand.sub_strands?.reduce((s_acc, sub) => s_acc + (sub.outcomes?.length || 0), 0) || 0), 0) || 0;
        const masteredOutcomesArea = area.strands?.reduce((acc, strand) =>
            acc + (strand.sub_strands?.reduce((s_acc, sub) => s_acc + (sub.outcomes?.filter(o => ['EE', 'ME'].includes(o.competency_level)).length || 0), 0) || 0), 0) || 0;
        const progressionRating = totalOutcomesArea > 0 ? (masteredOutcomesArea / totalOutcomesArea) * 100 : 0;
        const totalSubStrands = area.strands?.reduce((acc, strand) => acc + (strand.sub_strands?.length || 0), 0) || 0;
        const isPassing = progressionRating >= 80;

        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full flex flex-col md:flex-row md:items-center justify-between gap-6 px-8 py-6 bg-white rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-all group text-left"
                >
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="text-[10px] font-black text-[#FFC425] uppercase tracking-widest px-2 py-0.5 bg-[#FFC425]/10 rounded border border-[#FFC425]/20">
                                {area.code}
                            </span>
                            <div className="h-px bg-slate-100 flex-1"></div>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-black text-[#18216D] tracking-tight group-hover:text-blue-600 transition-colors uppercase">
                            {area.name}
                        </h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                            {totalSubStrands} Active Sub-Strands
                        </p>
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="text-right">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Academic Status</p>
                            <div className={`flex items-center gap-2 font-black text-sm uppercase tracking-widest ${isPassing ? 'text-emerald-500' : 'text-amber-500'}`}>
                                {isPassing ? (
                                    <>
                                        <TrophyIcon className="w-5 h-5" />
                                        On Track
                                    </>
                                ) : (
                                    <>
                                        <InformationCircleIcon className="w-5 h-5" />
                                        Developing
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="bg-[#18216D] text-white px-6 py-4 rounded-[2rem] text-center shadow-xl shadow-indigo-900/20 min-w-[100px]">
                                <p className="text-[8px] font-black text-indigo-300 uppercase tracking-[0.2em] mb-1">Mean Score</p>
                                <p className="text-xl font-black">{area.area_mean_score?.toFixed(2) || 'N/A'}</p>
                            </div>
                            <div className={`p-2 rounded-xl bg-slate-50 border border-slate-100 text-[#18216D] transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                <ChevronDownIcon className="w-5 h-5" />
                            </div>
                        </div>
                    </div>
                </button>

                <div className={`grid-rows-transition ${isExpanded ? 'grid-rows-expanded' : ''}`}>
                    <div className="overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-12 animate-in fade-in slide-in-from-top-4 duration-500 px-2 mt-4">
                            {area.strands?.flatMap(strand => strand.sub_strands || []).map((sub, idx) => (
                                <SubStrandCard key={idx} subStrand={sub} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const PerformanceHistoryTable = ({ history }) => {
        if (!history || history.length === 0) {
            return (
                <div className="bg-white rounded-[2rem] p-12 text-center border border-slate-100">
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No activity history found for this period.</p>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50">
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Activity</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Learning Area</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-center">Date</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-center">Score</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-right">Mastery</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {history.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.type === 'Assignment' ? 'bg-indigo-50 text-indigo-600' : 'bg-amber-50 text-amber-600'}`}>
                                                {item.type === 'Assignment' ? <DocumentTextIcon className="w-4 h-4" /> : <StarIcon className="w-4 h-4" />}
                                            </div>
                                            <div>
                                                <p className="text-sm font-black text-[#18216D] leading-none">{item.title}</p>
                                                <p className="text-[10px] font-bold text-slate-400 mt-1">{item.type}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="text-[10px] font-black text-slate-600 border border-slate-200 px-2 py-1 rounded bg-white uppercase">
                                            {item.learning_area_name}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <p className="text-xs font-bold text-slate-500">
                                            {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="bg-slate-100/50 px-3 py-1 rounded-lg inline-block">
                                            <span className="text-xs font-black text-[#18216D]">
                                                {item.score !== null ? `${item.score}/${item.total_marks}` : 'Grading...'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-right">
                                        <span className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest ${getGradeColor(item.competency_level)}`}>
                                            {item.competency_level || 'N/A'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#18216D]/10 border-t-[#18216D] mb-4"></div>
                <p className="text-[#18216D] font-black uppercase tracking-widest text-[10px]">Assembling Your Success Journey...</p>
            </div>
        );
    }

    if (!reportData) return null;

    const filteredLearningAreas = reportData.learning_areas?.filter(area =>
        area.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        area.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            {/* High-Density White Header */}
            <div className="bg-white border-b border-gray-100 relative z-30 shadow-sm">
                <div className="max-w-7xl mx-auto px-8 py-5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className={`${isMobile ? 'text-center' : ''}`}>
                            <div className={`flex items-center gap-3 mb-1 ${isMobile ? 'justify-center' : ''}`}>
                                <span className="text-[#18216D] text-[10px] font-black uppercase tracking-[0.2em]">Scholar Success Hub</span>
                                <div className="w-1 h-1 bg-slate-200 rounded-full" />
                                <span className="text-slate-400 text-[10px] uppercase font-bold tracking-widest">
                                    ID: {reportData.student?.student_id}
                                </span>
                                <div className="w-1 h-1 bg-slate-200 rounded-full" />
                                <span className="text-[#FFC425] text-[10px] uppercase font-black tracking-widest">
                                    Pathway: {reportData.student?.pathway}
                                </span>
                            </div>
                            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-black text-[#18216D] tracking-tight`}>Academic Mastery</h1>
                        </div>

                        <div className={`flex items-center gap-4 ${isMobile ? 'flex-col w-full' : ''}`}>
                            {!isMobile && (
                                <div className="text-right mr-4">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Scholar</p>
                                    <p className="text-sm font-black text-[#18216D]">{reportData.student?.name}</p>
                                </div>
                            )}
                            <div className={`flex items-center gap-4 ${isMobile ? 'w-full flex-col' : ''}`}>
                                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 md:pb-0">
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => {
                                            setSelectedYear(e.target.value);
                                            setSelectedTerm(''); // Reset term when year changes
                                        }}
                                        className="px-4 py-3 bg-white border border-slate-200 rounded-xl font-bold text-[#18216D] text-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 transition-all shadow-sm hover:shadow-md min-w-[140px]"
                                    >
                                        <option value="">Select Year</option>
                                        {academicYears.map(year => (
                                            <option key={year.id} value={year.id}>{year.name}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={selectedTerm}
                                        onChange={(e) => setSelectedTerm(e.target.value)}
                                        className="px-4 py-3 bg-white border border-slate-200 rounded-xl font-bold text-[#18216D] text-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 transition-all shadow-sm hover:shadow-md min-w-[140px]"
                                    >
                                        <option value="">All Terms</option>
                                        {terms
                                            .filter(term => !selectedYear || term.year.toString() === selectedYear)
                                            .map(term => (
                                                <option key={term.id} value={term.id}>{term.name}</option>
                                            ))}
                                    </select>
                                </div>
                                <button
                                    onClick={handleDownloadReport}
                                    className={`flex items-center justify-center gap-2 bg-[#FFC425] text-[#18216D] px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#18216D] hover:text-white transition-all shadow-lg shadow-amber-900/10 group ${isMobile ? 'w-full' : ''}`}
                                >
                                    <ArrowDownTrayIcon className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-8 pt-12">
                {/* Stats Overview */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-14">
                    {/* Card 1: Academic Profile */}
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                        <div className="relative z-10 text-left">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-12 h-12 bg-[#18216D] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-900/40">
                                    <AcademicCapIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Academic Profile</h3>
                                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">Competency Breakdown</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-4">
                                {[
                                    { level: 'EE', label: 'Exceeding' },
                                    { level: 'ME', label: 'Meeting' },
                                    { level: 'AE', label: 'Approaching' },
                                    { level: 'BE', label: 'Below' }
                                ].map((item) => {
                                    const data = getLevelData(item.level);
                                    return (
                                        <div key={item.level} className={`p-4 ${data.bg} rounded-2xl border ${data.border}`}>
                                            <p className={`text-[9px] font-black ${data.color} uppercase tracking-widest mb-1`}>{item.label}</p>
                                            <p className={`text-2xl font-black ${data.color}`}>{reportData.overall_stats?.breakdown?.[item.level] || 0}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Engagement & Performance */}
                    {!isMobile && (
                        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-900/20">
                                        <ChartBarIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Engagement</h3>
                                        <p className="text-[10px] font-bold text-slate-400 mt-0.5">Performance & Attendance</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex items-center gap-3">
                                            <StarIcon className="w-5 h-5 text-indigo-500" />
                                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Avg Performance</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {(() => {
                                                const code = reportData.summary_stats?.average_performance_level_8 ||
                                                    reportData.summary_stats?.average_performance_level ||
                                                    'N/A';
                                                const colors = getGradeColor(code).split(' ');
                                                const textColor = colors.find(c => c.startsWith('text-'));
                                                return (
                                                    <>
                                                        <span className={`text-2xl font-black ${textColor}`}>
                                                            {code}
                                                        </span>
                                                        <span className={`text-[10px] font-bold text-[#18216D]`}>
                                                            MS: {reportData.summary_stats?.average_performance_mean_score?.toFixed(2) || 'N/A'}
                                                        </span>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex items-center gap-3">
                                            <ClockIcon className="w-5 h-5 text-emerald-500" />
                                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Attendance</span>
                                        </div>
                                        <p className="text-lg font-black text-[#18216D]">
                                            {reportData.summary_stats?.attendance_present || 0}/{reportData.summary_stats?.attendance_total || 0}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Learning Areas</p>
                                            <p className="text-lg font-black text-[#18216D]">{reportData.summary_stats?.total_learning_areas}</p>
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Activities Done</p>
                                            <p className="text-lg font-black text-[#18216D]">{reportData.overall_stats?.total_activities}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Card 3: Top Specialities */}
                    {!isMobile && (
                        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10 text-left">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 bg-[#FFC425] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-amber-900/40">
                                        <TrophyIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Specialities</h3>
                                        <p className="text-[10px] font-bold text-slate-400 mt-0.5">Top Strengths</p>
                                    </div>
                                </div>

                                <div className="space-y-4 text-left">
                                    {reportData.summary_stats?.specialities?.length > 0 ? reportData.summary_stats.specialities.map((area, idx) => (
                                        <div key={area.code} className="flex items-center gap-4 p-3 bg-amber-50/30 rounded-2xl border border-amber-100/50">
                                            <div className="w-10 h-10 rounded-xl bg-[#FFC425] flex items-center justify-center text-white font-black text-xs shadow-sm">
                                                #{idx + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{area.code}</p>
                                                <p className="text-xs font-bold text-[#18216D] truncate">{area.name}</p>
                                                <p className="text-[8px] font-black text-[#FFC425] uppercase tracking-widest">{area.breakdown?.EE || 0} EE Outcomes</p>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="py-10 text-center">
                                            <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No core specialities identified yet</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Progress Content */}
                <div className="space-y-12">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        <div className="flex-1">
                            <div className="flex flex-col md:flex-row md:items-center gap-6">
                                <div>
                                    <h2 className="text-3xl font-black text-[#18216D] tracking-tight flex items-center gap-3">
                                        {viewMode === 'matrix' ? 'Competency Mastery Matrix' : 'Performance History'}
                                    </h2>
                                    <p className="text-slate-400 font-bold text-sm mt-1">
                                        {viewMode === 'matrix' ? 'Your academic progression across all sub-strands' : 'Detailed record of all assessments and activities'}
                                    </p>
                                </div>
                                {viewMode === 'matrix' && (
                                    <div className="relative flex-1 max-w-md">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <MagnifyingGlassIcon className="h-5 w-5 text-slate-300" />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search learning areas..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="block w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-bold text-[#18216D] placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-200 transition-all shadow-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Desktop-only Toggles */}
                            {!isMobile && (
                                <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200">
                                    <button
                                        onClick={() => setViewMode('matrix')}
                                        className={`flex items-center gap-2 px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'matrix' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        <TableCellsIcon className="w-4 h-4" />
                                        Matrix
                                    </button>
                                    <button
                                        onClick={() => setViewMode('history')}
                                        className={`flex items-center gap-2 px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'history' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        <ListBulletIcon className="w-4 h-4" />
                                        History
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-16">
                        {viewMode === 'matrix' ? (
                            <>
                                {filteredLearningAreas.map((area) => (
                                    <LearningAreaAccordion key={area.code} area={area} />
                                ))}
                                {filteredLearningAreas.length === 0 && (
                                    <div className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-slate-200">
                                        <MagnifyingGlassIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                                        <h3 className="text-xl font-black text-[#18216D] mb-1">No matches found</h3>
                                        <p className="text-slate-400 font-bold text-sm">We couldn't find any learning areas matching "{searchTerm}"</p>
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="mt-6 text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline"
                                        >
                                            Clear search
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <PerformanceHistoryTable history={reportData.performance_history} />
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SuccessHub;
